// P4-SCHED Release 2-B — MEP quantities for the execution schedule
// (SCHEDULE_INTELLIGENCE_MODULE.md §4 "Future HVAC / electrical / utility
// mappings", §5, §42, §51). Pure: reads the revision's canonical MEP records
// and returns the installation quantities the schedule ruleset turns into
// activities. Nothing is sized, priced or invented here — a run without a
// length, a route on a non-base scenario or an existing record's connection is
// reported, never guessed.
//
// Quantity sources (all existing records):
//   routes        UtilityRouteSegments (db/24 / db/77) — effective LF per segment
//                 = OverrideLF, else the drawn polyline (polylineLengthFt). The
//                 installation duration does not depend on the pipe size, so an
//                 unsized (Sizing Required) run still schedules; only an
//                 unmeasured one cannot. Utility-owned segments group by their
//                 utility's kind; HVAC-owned segments count only when their
//                 scenario is the selected base (the same rule the Estimate
//                 prices by) — chilled water → hydronic, DX → refrigerant line
//                 sets (packaged rooftop has no field piping).
//   panels        EstimatePanels (db/24) — new panels (IsExisting = 0).
//   circuits      EstimateCircuits — every circuit with a breaker (a spare, or a
//                 row with no trip size, is not wired).
//   connections   EquipmentConnections × the record's installed Qty, for records
//                 procurementScope() puts in this contract — electrical rows
//                 (terminations) apart from utility rows (final hook-ups).
// Controls points have no source record yet — nothing is generated for them.

import { polylineLengthFt } from "./pipeSizing";
import { procurementScope } from "./procurementSchedule";
import { UTILITY_BY_KEY, utilityLabel } from "../config/utilityTypes";
import { refrigerantLinesFor } from "../config/systemArchitectures";

const r1 = (v) => Math.round(v * 10) / 10;
const num = (v) => (v == null || v === "" || !Number.isFinite(+v) ? null : +v);

// Piping / duct group a utility's route installs under (one activity type
// each): hydronic (CHW / HHW), process_water, gas (process gas / vacuum),
// steam, drain, duct (process exhaust / vent).
const HYDRONIC = new Set(["chilled_water", "heating_water"]);
export function pipingGroupFor(utilityType) {
  if (HYDRONIC.has(utilityType)) return "hydronic";
  const kind = UTILITY_BY_KEY[utilityType]?.kind;
  if (kind === "liquid") return "process_water";
  if (kind === "gas") return "gas";
  if (kind === "steam") return "steam";
  if (kind === "drain") return "drain";
  if (kind === "air") return "duct";
  return null; // electrical / unknown — no route activity
}

export const segmentLengthFt = (s) => num(s.OverrideLF) ?? polylineLengthFt(s.GeometryJson);

const equipmentLabel = (e) => e ? (e.Tag ? `${e.Tag}${e.Description ? ` · ${e.Description}` : ""}` : (e.Description || `Equipment #${e.EquipmentID}`)) : "equipment";

// → { piping: { [group]: { qty, detail } }, refrigerant: { qty, detail },
//     panels: { count, detail }, circuits: { count, detail },
//     connections: { electrical: { count, detail, equipmentIds }, utility: { … } },
//     unscheduled: [{ code, message, … }] }
// detail rows: { label, roomName, qty, … } — the schedule's "why?" table shape.
export function mepTakeoff({
  routeSegments = [], utilitySystems = [], scenarios = [], hvacSystems = [],
  panels = [], circuits = [], equipment = [], connections = [], rooms = [],
} = {}) {
  const roomName = (id) => (id == null ? null : rooms.find(r => r.RoomID === id)?.RoomName || null);
  const unscheduled = [];
  const piping = {};
  const refrigerant = { qty: 0, detail: [] };

  // ── Routes ────────────────────────────────────────────────────────────────
  const utilityById = new Map(utilitySystems.map(u => [u.UtilitySystemID, u]));
  const hvacById = new Map(hvacSystems.map(h => [h.SystemID, h]));
  const scenarioById = new Map(scenarios.map(s => [s.ScenarioID, s]));
  const groups = new Map(); // detail key → { target, label, qty, segments, unmeasured }
  let offBase = 0, orphan = 0;
  for (const s of routeSegments) {
    let target = null, key = null, label = null, group = null;
    if (s.HvacSystemID != null) {
      const h = hvacById.get(s.HvacSystemID);
      const sc = h ? scenarioById.get(h.ScenarioID) : null;
      if (!h || !sc) { orphan++; continue; }
      if (sc.Status !== "selected_base") { offBase++; continue; }
      if (h.ArchitectureType === "chilled_water") { group = "hydronic"; label = `Chilled water · ${h.SystemKey} (${sc.ScenarioKey} base)`; }
      else if (h.ArchitectureType === "dx") {
        if (!refrigerantLinesFor(h.DistributionType).length) continue; // packaged rooftop: factory-charged, no field piping
        target = "refrigerant"; label = `Refrigerant line sets · ${h.SystemKey} (${sc.ScenarioKey} base)`;
      } else { orphan++; continue; }
      key = `hvac:${h.SystemID}`;
    } else {
      const u = utilityById.get(s.UtilitySystemID);
      if (!u) { orphan++; continue; }
      group = pipingGroupFor(u.UtilityType);
      if (!group) continue;
      label = utilityLabel(u.UtilityType);
      key = `util:${u.UtilitySystemID}`;
    }
    const g = groups.get(key) || { target: target || `piping:${group}`, group, label, qty: 0, segments: 0, unmeasured: 0 };
    const lf = segmentLengthFt(s);
    g.segments++;
    if (lf == null || !(lf > 0)) g.unmeasured++;
    else g.qty += lf;
    groups.set(key, g);
  }
  for (const g of groups.values()) {
    if (g.unmeasured) unscheduled.push({ code: "route_length_missing", message: `${g.label}: ${g.unmeasured} of ${g.segments} route segment${g.segments === 1 ? "" : "s"} have no length — draw the route or enter an LF (Assumed Equipment ▸ Distribution) so it can be scheduled.`, label: g.label, count: g.unmeasured });
    if (!(g.qty > 0)) continue;
    const row = { label: g.label, roomName: null, qty: r1(g.qty), segments: g.segments, unmeasuredSegments: g.unmeasured };
    if (g.target === "refrigerant") { refrigerant.qty += g.qty; refrigerant.detail.push(row); }
    else { const b = piping[g.group] ||= { qty: 0, detail: [] }; b.qty += g.qty; b.detail.push(row); }
  }
  refrigerant.qty = r1(refrigerant.qty);
  for (const b of Object.values(piping)) b.qty = r1(b.qty);
  if (offBase) unscheduled.push({ code: "route_not_base_scenario", message: `${offBase} HVAC route segment${offBase === 1 ? " belongs" : "s belong"} to a design scenario that is not the selected base — not scheduled (select a base scenario, or add a manual activity for an alternate).`, count: offBase });
  if (orphan) unscheduled.push({ code: "route_owner_missing", message: `${orphan} route segment${orphan === 1 ? " has" : "s have"} no resolvable utility or HVAC system — not scheduled.`, count: orphan });

  // ── Electrical distribution ───────────────────────────────────────────────
  const panelById = new Map(panels.map(p => [p.PanelID, p]));
  const newPanels = panels.filter(p => !p.IsExisting);
  const panelsOut = { count: newPanels.length, detail: newPanels.map(p => ({ panelId: p.PanelID, label: p.PanelName, roomName: roomName(p.RoomID) || p.LocationText || null, qty: 1 })) };
  const wired = circuits.filter(c => String(c.LoadClass || "").toLowerCase() !== "spare" && c.TripAmps != null && c.TripAmps !== "");
  const byPanel = new Map();
  for (const c of wired) {
    const e = byPanel.get(c.PanelID) || { panelId: c.PanelID, label: panelById.get(c.PanelID)?.PanelName || `Panel #${c.PanelID}`, roomName: null, qty: 0, routeLf: 0 };
    e.qty++; e.routeLf += num(c.RouteLF) || 0;
    byPanel.set(c.PanelID, e);
  }
  const circuitsOut = { count: wired.length, detail: [...byPanel.values()].map(e => ({ ...e, routeLf: r1(e.routeLf) })) };

  // ── Equipment connections ─────────────────────────────────────────────────
  const inScope = new Map(procurementScope(equipment).map(e => [e.EquipmentID, e]));
  const conn = { electrical: { count: 0, detail: [], equipmentIds: [] }, utility: { count: 0, detail: [], equipmentIds: [] } };
  const existingWithConnections = new Set();
  const perEquip = new Map(); // `${side}:${EquipmentID}` → detail row
  for (const c of connections) {
    const e = inScope.get(c.EquipmentID);
    if (!e) { if (equipment.some(x => x.EquipmentID === c.EquipmentID && String(x.Status || "").toLowerCase() === "existing")) existingWithConnections.add(c.EquipmentID); continue; }
    const side = c.UtilityType === "electrical" ? "electrical" : "utility";
    const n = +e.Qty || 0;
    if (!(n > 0)) continue;
    const k = `${side}:${e.EquipmentID}`;
    const row = perEquip.get(k) || { equipmentId: e.EquipmentID, label: equipmentLabel(e), roomName: roomName(e.RoomID) || e.LocationText || null, qty: 0, connections: 0, utilities: [] };
    row.qty += n; row.connections++;
    if (side === "utility" && !row.utilities.includes(c.UtilityType)) row.utilities.push(c.UtilityType);
    perEquip.set(k, row);
    conn[side].count += n;
  }
  for (const [k, row] of perEquip) {
    const side = k.startsWith("electrical:") ? "electrical" : "utility";
    conn[side].detail.push(row);
    conn[side].equipmentIds.push(row.equipmentId);
  }
  if (existingWithConnections.size) unscheduled.push({ code: "existing_equipment_connections", message: `${existingWithConnections.size} existing equipment record${existingWithConnections.size === 1 ? " has" : "s have"} connections — not scheduled (add a manual activity if they are being reconnected).`, count: existingWithConnections.size });

  return { piping, refrigerant, panels: panelsOut, circuits: circuitsOut, connections: conn, unscheduled };
}

// The ruleset's `source` string → { qty, detail } (null when that scope does not exist).
export function mepQuantityFor(takeoff, source) {
  if (!takeoff || !source?.startsWith("mep:")) return null;
  const [, what, sub] = source.split(":");
  if (what === "piping") { const b = takeoff.piping[sub]; return b ? { qty: b.qty, detail: b.detail } : null; }
  if (what === "refrigerant") return { qty: takeoff.refrigerant.qty, detail: takeoff.refrigerant.detail };
  if (what === "panels") return { qty: takeoff.panels.count, detail: takeoff.panels.detail };
  if (what === "circuits") return { qty: takeoff.circuits.count, detail: takeoff.circuits.detail };
  if (what === "connections") { const b = takeoff.connections[sub]; return b ? { qty: b.count, detail: b.detail } : null; }
  return null;
}

export const EMPTY_MEP_TAKEOFF = Object.freeze(mepTakeoff());
